import * as Sentry from "@sentry/browser";
import axios, { AxiosProgressEvent } from "axios";
import React from "react";
import uuid from "react-uuid";
import useRequest from "src/hooks/useRequest";

import ProgressBar from "./ProgressBar";
import TickIcon from "../../../assets/icons/tickIcon.svg?react";
import { colors } from "../../../constants/colorPalette";
import { useWakeLock } from "../../../hooks/useWakeLock";
import {
  FileUploadType,
  ID,
  InputChangeEvent,
  UploadProgress,
  UploadUrlResponse,
} from "../../../types/index";

interface Props {
  fileId: ID;
  courseId: ID;
  fileType: FileUploadType;
  abortController: AbortController;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileUploaded: (fileId: ID, videoUrl: string) => void;
  onFileUploadProgress: (data: AxiosProgressEvent, fileId: ID) => void;
  uploaded: boolean;
  uploadProgress: UploadProgress;
  onClickCancelFileUpload: React.MouseEventHandler<HTMLButtonElement>;
  onNewFileSelected: (newFileId: ID, oldId: ID) => void;
}

const fileTypeMap = {
  [FileUploadType.Video]: {
    endpoint: "/get-video-upload-url",
    label: "Video",
    accept: "video/mp4, video/quicktime",
    contentType: "multipart/form-data",
  },
  [FileUploadType.Material]: {
    endpoint: "/get-material-upload-url",
    label: "File",
    accept: "application/pdf",
    contentType: "application/pdf",
  },
};

const FilePicker: React.FC<Props> = ({
  fileType,
  fileId,
  courseId,
  abortController,
  fileInputRef,
  onFileUploaded,
  onFileUploadProgress,
  uploaded,
  uploadProgress,
  onClickCancelFileUpload,
  onNewFileSelected,
}) => {
  const { releaseWakeLock, requestWakeLock } = useWakeLock();
  const { endpoint, label, accept, contentType } = fileTypeMap[fileType];

  const requestUploadUrl = useRequest(endpoint);

  const uploadFileToS3 = async (uploadUrl: string, file: File) => {
    requestWakeLock();

    try {
      await axios.put(uploadUrl, file, {
        headers: {
          "Content-Type": contentType,
        },
        signal: abortController.signal,
        onUploadProgress: (data: AxiosProgressEvent) =>
          onFileUploadProgress(data, fileId),
      });

      releaseWakeLock();

      const videoUrl = uploadUrl.split("?")[0];

      onFileUploaded(fileId, videoUrl);
    } catch (error) {
      releaseWakeLock();

      Sentry.captureException(error);
      console.log(error);
    }
  };

  const onSuccess = (e: InputChangeEvent, result: UploadUrlResponse) => {
    const files = e.target?.files || [];

    uploadFileToS3(result.uploadUrl, files[0]);
  };

  const onError = (error: string) => {
    Sentry.captureException(error);
    console.log(error);
  };

  const handleFileSelected = (e: InputChangeEvent) => {
    const storageKey = uuid();

    onNewFileSelected(fileId, storageKey);

    requestUploadUrl({
      requestBody: {
        storageKey,
        courseId,
      },
      onSuccess: (result: any) => onSuccess(e, result),
      onError,
    });
  };

  const renderUploadProgressIcon = () => {
    if (uploaded) {
      return (
        <TickIcon stroke={colors.green} className="tick-upload-icon ms-2" />
      );
    }

    if (!uploadProgress) {
      return null;
    }

    if (uploadProgress !== 1) {
      return (
        <div className="upload-file-icons-container">
          <ProgressBar value={uploadProgress} />
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm ms-3"
            onClick={onClickCancelFileUpload}
          >
            Cancel
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="d-flex align-items-center mt-3">
      <label htmlFor={`inputTag-${fileId}`} className="secondary-btn">
        Select {label}
        <input
          ref={fileInputRef}
          name="file-picker"
          className="file-picker"
          id={`inputTag-${fileId}`}
          type="file"
          accept={accept}
          onChange={handleFileSelected}
        />
      </label>
      {renderUploadProgressIcon()}
    </div>
  );
};

export default FilePicker;
